import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { get as idbGet, set as idbSet } from "idb-keyval";
import { toast } from "sonner";
import { api, getTokens, setTokens, setApiStaff, errMsg } from "@/lib/api";
import { applyLocal, mergePull, emptyBase } from "@/lib/ops";
import { translate, LANGS, MEDICAL_TYPES } from "@/lib/i18n";
import { syncErrText } from "@/lib/syncErrors";
import { startHeartbeat } from "@/lib/telemetry";
import { setPrintBrand } from "@/lib/print";
import { fileUrl } from "@/lib/files";
import { can as canRole, OP_PERM, canOp, canStaff } from "@/lib/perms";
import { fmtNum, sha256, uuid } from "@/lib/format";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

const IDLE_MS = 10 * 60 * 1000;
const DEVICE_ID = localStorage.getItem("sp_device") || uuid();
localStorage.setItem("sp_device", DEVICE_ID);

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => getTokens()?.user || null);
  const [lang, setLang] = useState(() => { const l = localStorage.getItem("sp_lang"); return l && l !== "mey" ? l : "ar"; });
  const [base, setBase] = useState(emptyBase);
  const [queue, setQueue] = useState([]);
  const [failed, setFailed] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [netOnline, setNetOnline] = useState(navigator.onLine);
  const [forceOffline, setForceOfflineS] = useState(() => localStorage.getItem("sp_force_offline") === "1");
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [persisted, setPersisted] = useState(null);
  const [offlineSince, setOfflineSince] = useState(() => (navigator.onLine ? null : Date.now()));
  const [staff, setStaffS] = useState(null);
  const refs = useRef({ base: emptyBase(), queue: [], failed: [], syncing: false, lastActive: Date.now(), fails: 0, nextTry: 0 });
  const online = netOnline && !forceOffline;
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const langRef = useRef(lang);
  langRef.current = lang;
  const sid = user?.shop_id;

  const medical = MEDICAL_TYPES.includes(base.shop?.type);
  const t = useCallback((k, v) => translate(lang, k, v, medical), [lang, medical]);
  const dir = LANGS.find((l) => l.code === lang)?.dir || "rtl";

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = ["es", "fr"].includes(lang) ? lang : "ar";
    localStorage.setItem("sp_lang", lang);
  }, [lang, dir]);

  useEffect(() => (window.location.pathname === "/display" ? undefined : startHeartbeat("app", () => user?.shop_id)), [user?.shop_id]);

  const write = useCallback((key, val) => {
    refs.current[key] = val;
    ({ base: setBase, queue: setQueue, failed: setFailed })[key](val);
    if (sid) idbSet(`${key}:${sid}`, val).catch(() => {});
  }, [sid]);

  const setStaff = useCallback((s) => {
    setStaffS(s);
    setApiStaff(s?.id);
    refs.current.lastActive = Date.now();
    if (s) localStorage.setItem("sp_staff", JSON.stringify({ id: s.id, at: Date.now() }));
    else localStorage.removeItem("sp_staff");
  }, []);

  const logout = useCallback(() => {
    setTokens(null);
    setUser(null);
    setStaff(null);
    setLoaded(false);
    setBase(emptyBase());
    setQueue([]);
    refs.current = { ...refs.current, base: emptyBase(), queue: [], failed: [] };
  }, [setStaff]);

  useEffect(() => {
    if (!sid) return undefined;
    let alive = true;
    setLoaded(false);
    Promise.all(["base", "queue", "failed"].map((k) => idbGet(`${k}:${sid}`).catch(() => null))).then(([b, q, f]) => {
      if (!alive) return;
      write("base", b || emptyBase());
      write("queue", q || []);
      write("failed", f || []);
      const saved = JSON.parse(localStorage.getItem("sp_staff") || "null");
      // Restore the staff session only within the same app session (tab/window). A fresh open always shows the staff picker.
      const sameSession = sessionStorage.getItem("sp_session") === "1";
      const member = sameSession && saved && Date.now() - saved.at < IDLE_MS && (b?.staff || []).find((s) => s.id === saved.id && s.active);
      sessionStorage.setItem("sp_session", "1");
      if (member) setStaff(member); else localStorage.removeItem("sp_staff");
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [sid, write, setStaff]);

  useEffect(() => {
    if (!navigator.storage?.persist) return;
    navigator.storage.persisted().then((p) => (p ? setPersisted(true) : navigator.storage.persist().then(setPersisted))).catch(() => setPersisted(false));
  }, []);

  const syncNow = useCallback(async (force = false) => {
    const r = refs.current;
    if (r.syncing || !onlineRef.current || !sid || !getTokens()) return;
    if (!force && Date.now() < r.nextTry) return;
    r.syncing = true;
    setSyncing(true);
    try {
      while (r.queue.length) {
        const batch = r.queue.slice(0, 25);
        const { data } = await api.post("/sync/push", { device_id: DEVICE_ID, ops: batch.map(({ staff_name, ...o }) => o) });
        const done = new Set(data.results.map((x) => x.op_id));
        const errs = data.results.filter((x) => x.status === "error").map((x) => ({ ...x, op: batch.find((o) => o.op_id === x.op_id) }));
        if (errs.length) {
          write("failed", [...errs, ...r.failed].slice(0, 100));
          const lim = errs.find((x) => String(x.error).startsWith("plan_limit"));
          const first = errs[0];
          toast.error(lim ? translate(langRef.current, lim.error) : `${translate(langRef.current, "sync_failed_ops", { n: errs.length })} · ${translate(langRef.current, `act_${first.op?.type}`)}: ${syncErrText(langRef.current, first.error)}`, { duration: 8000 });
        }
        if (data.results.some((x) => x.status === "conflict")) toast.warning(translate(langRef.current, "sync_conflicts"));
        write("queue", r.queue.filter((o) => !done.has(o.op_id)));
      }
      const since = r.base.lastSync && !r.fullResync ? new Date(new Date(r.base.lastSync).getTime() - 90000).toISOString() : null;
      r.fullResync = false;
      const { data } = await api.get("/sync/pull", { params: since ? { since } : {} });
      write("base", mergePull(r.base, data));
      r.fails = 0; r.nextTry = 0;
      setSyncError(null);
    } catch (e) {
      const code = e.response?.data?.detail;
      r.fails += 1;
      r.nextTry = Date.now() + Math.min(5 * 60 * 1000, 5000 * 2 ** Math.min(r.fails, 6));
      setSyncError(code === "plan_limit_devices" ? translate(langRef.current, code) : errMsg(e));
      if (code === "plan_limit_devices") toast.error(translate(langRef.current, code), { id: code });
      if (e.response?.status === 423 && !String(code).startsWith("account_")) {
        toast.error(translate(langRef.current, "store_suspended"));
        logout();
      }
      if (e.response?.status === 401) {
        const n = r.queue.length;
        toast.error(n ? translate(langRef.current, "session_expired_pending", { n }) : translate(langRef.current, "session_expired"), { duration: 8000 });
        logout();
      }
    } finally {
      r.syncing = false;
      setSyncing(false);
    }
  }, [sid, write, logout]);

  useEffect(() => {
    const up = () => { setNetOnline(true); setOfflineSince(null); refs.current.nextTry = 0; };
    const down = () => { setNetOnline(false); setOfflineSince((s) => s || Date.now()); };
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  useEffect(() => {
    if (!loaded || !online) return undefined;
    syncNow(true);
    const id = setInterval(() => syncNow(), 20000);
    const vis = () => { if (document.visibilityState === "visible") syncNow(true); };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", vis); };
  }, [loaded, online, syncNow]);

  useEffect(() => {
    if (!staff) return undefined;
    const touch = () => { refs.current.lastActive = Date.now(); };
    window.addEventListener("pointerdown", touch);
    window.addEventListener("keydown", touch);
    const id = setInterval(() => {
      if (Date.now() - refs.current.lastActive > IDLE_MS) setStaff(null);
      else localStorage.setItem("sp_staff", JSON.stringify({ id: staff.id, at: refs.current.lastActive }));
    }, 30000);
    return () => { clearInterval(id); window.removeEventListener("pointerdown", touch); window.removeEventListener("keydown", touch); };
  }, [staff, setStaff]);

  const can = useCallback((perm) => !perm ? !!staff : canStaff(staff, perm, refs.current.base?.shop), [staff, base]);

  const dispatchAs = useCallback((actor, type, data) => {
    if (!actor) return false;
    if (!canOp(actor, type, refs.current.base?.shop)) {
      toast.error(translate(langRef.current, "not_allowed"));
      return false;
    }
    const op = { op_id: uuid(), type, staff_id: actor.id, staff_name: actor.name, ts: new Date().toISOString(), data };
    write("queue", [...refs.current.queue, op]);
    setTimeout(() => syncNow(true), 600);
    return true;
  }, [write, syncNow]);
  const dispatch = useCallback((type, data) => dispatchAs(staff, type, data), [staff, dispatchAs]);

  const retryFailed = useCallback(() => {
    const r = refs.current;
    const retry = r.failed.filter((f) => f.op && String(f.error).startsWith("server error"));
    if (!retry.length) { write("failed", []); return; }
    write("queue", [...r.queue, ...retry.map((f) => ({ ...f.op, op_id: uuid() }))]);
    write("failed", r.failed.filter((f) => !retry.includes(f)));
    setTimeout(() => syncNow(true), 300);
  }, [write, syncNow]);

  const authWith = (data) => { setTokens(data); setUser(data.user); };
  const login = async (email, password) => authWith((await api.post("/auth/login", { email, password })).data);
  const register = async (form) => authWith((await api.post("/auth/register", form)).data);
  const verifyPin = async (member, pin) => (await sha256(`${sid}:${pin}`)) === member.pin_local;
  const setForceOffline = (v) => { localStorage.setItem("sp_force_offline", v ? "1" : "0"); setForceOfflineS(v); };
  const clearFailed = () => write("failed", []);
  const discardPending = useCallback((opId) => write("queue", refs.current.queue.filter((o) => o.op_id !== opId)), [write]);
  const discardFailed = useCallback((opId) => write("failed", refs.current.failed.filter((f) => f.op_id !== opId)), [write]);
  const retryOne = useCallback((opId) => {
    const r = refs.current;
    const f = r.failed.find((x) => x.op_id === opId);
    if (!f?.op) return;
    write("failed", r.failed.filter((x) => x.op_id !== opId));
    write("queue", [...r.queue, { ...f.op, op_id: uuid() }]);
    setTimeout(() => syncNow(true), 300);
  }, [write, syncNow]);
  const fullResync = useCallback(() => { refs.current.fullResync = true; refs.current.nextTry = 0; syncNow(true); }, [syncNow]);
  useEffect(() => {
    if (!online || !loaded) return undefined;
    const id = setInterval(() => {
      const q = refs.current.queue;
      if (q.length && Date.now() - new Date(q[0].ts).getTime() > 30 * 60 * 1000) toast.warning(translate(langRef.current, "sync_stale_warn", { n: q.length }), { id: "stale-sync", duration: 10000 });
      if (q.length > 300) toast.warning(translate(langRef.current, "sync_queue_big", { n: q.length }), { id: "big-queue", duration: 10000 });
    }, 60000);
    return () => clearInterval(id);
  }, [online, loaded]);

  const view = useMemo(() => { const v = queue.reduce(applyLocal, base); const ex = v.expenses || {}; const live = Object.fromEntries(Object.entries(ex).filter(([, e]) => !e.deleted)); return Object.keys(live).length === Object.keys(ex).length ? v : { ...v, expenses: live }; }, [base, queue]);
  const currency = view.shop?.currency || "DZD";
  useEffect(() => {
    const s = view.shop;
    if (!s) { setPrintBrand(null); return; }
    const apply = (logoUrl) => setPrintBrand({ name: s.name, address: s.address || s.city, phone: s.phone, logoUrl });
    const toData = (u) => fetch(u).then((r) => r.blob()).then((b) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(u); fr.readAsDataURL(b); }));
    if (s.logo_id) fileUrl(s.logo_id).then(toData).then(apply).catch(() => apply(null)); else apply(null);
  }, [view.shop?.id, view.shop?.name, view.shop?.logo_id, view.shop?.address, view.shop?.phone]); // eslint-disable-line
  const money = useCallback((n) => `${fmtNum(n)} ${currency}`, [currency]);

  const value = {
    user, login, register, logout, lang, setLang, t, dir, view, base, queue, failed, clearFailed, retryFailed, loaded, persisted, offlineSince,
    online, netOnline, forceOffline, setForceOffline, syncing, syncError, syncNow: () => syncNow(true), staff, setStaff, verifyPin,
    dispatch, dispatchAs, can, money, currency, discardPending, discardFailed, retryOne, fullResync, deviceId: DEVICE_ID,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
